import React, { useState, useEffect } from 'react';
import { Select, DatePicker, Spin, message, Button, Table, Modal } from 'antd';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    getHotelsList,
    getOutletsList,
    getAnalyticsData,
} from '../services/apiservice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import Header from '../components/Header';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const { RangePicker } = DatePicker;

const Analytics = () => {
    const [loading, setLoading] = useState(false);
    const [hotels, setHotels] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [selectedHotels, setSelectedHotels] = useState(['all']);
    const [selectedOutlets, setSelectedOutlets] = useState(['all']);
    const [intervalType, setIntervalType] = useState('weekly');
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(1, 'month'),
        dayjs(),
    ]);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [allHotelIds, setAllHotelIds] = useState([]);
    const [tableData, setTableData] = useState([]);

    const dateFormatters = {
        monthly: {
            format: (date) => dayjs(date).format('MMM YYYY'),
            groupBy: (date) =>
                dayjs(date).startOf('month').format('DD-MM-YYYY'),
        },
        weekly: {
            format: (date) => {
                const weekNumber = dayjs(date).week();
                const startOfWeek = dayjs(date).startOf('week');
                const endOfWeek = dayjs(date).endOf('week');
                return `W-${weekNumber} (${startOfWeek.format('DD MMM')} - ${endOfWeek.format('DD MMM')})`;
            },
            groupBy: (date) => dayjs(date).startOf('week').format('DD-MM-YYYY'),
        },
        daily: {
            format: (date) => dayjs(date).format('DD MMM'),
            groupBy: (date) => dayjs(date).format('DD MMM'),
        },
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: {
                    font: { size: 14 },
                    color: 'black',
                    padding: 20, // Padding around legend items (including bottom)
                },
            },
            tooltip: {
                enabled: true, // Make sure tooltips are enabled
                mode: 'nearest', // Display tooltip nearest to the hover point
                intersect: false, // Tooltip will show when hovering over the graph and not just directly on points
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.raw;
                        return `${label}: ${value}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Footfall',
                    font: { size: 20 },
                    color: '#FF5733',
                },
                ticks: { color: '#000', stepSize: 1, font: { size: 12 } },
            },
            x: {
                title: {
                    display: true,
                    text: 'Date',
                    font: { size: 20 },
                    color: '#3498DB',
                },
                ticks: { color: '#000', font: { size: 12 } },
            },
        },
        layout: {
            padding: {
                bottom: 40,
                left: 20,
                right: 20,
            },
        },
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
    };

    useEffect(() => {
        fetchHotels();
    }, []);

    useEffect(() => {
        if (selectedHotels.length) {
            fetchOutlets();
        } else {
            setOutlets([]);
            setSelectedOutlets([]);
        }
    }, [selectedHotels]);

    useEffect(() => {
        if (selectedHotels.length && allHotelIds.length) {
            fetchAnalytics();
        }
    }, [selectedHotels, selectedOutlets, intervalType, dateRange, allHotelIds]);

    const fetchHotels = async () => {
        try {
            setLoading(true);
            const response = await getHotelsList();
            const hotelsList = response.data.map(
                ({ hotel_name, hotel_id }) => ({
                    label: hotel_name,
                    value: hotel_id,
                })
            );
            setHotels([{ label: 'All Hotels', value: 'all' }, ...hotelsList]);
            setAllHotelIds(response.data.map((hotel) => hotel.hotel_id));
        } catch (error) {
            message.error('Failed to fetch hotels. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchOutlets = async () => {
        try {
            const response = await getOutletsList();
            // If 'All Hotels' is selected, show all restaurants
            if (selectedHotels.includes('all')) {
                const outletsList = response.data.map(
                    ({ restaurant_name, restaurant_id }) => ({
                        label: restaurant_name,
                        value: restaurant_id,
                    })
                );
                setOutlets([
                    { label: 'All Restaurants', value: 'all' },
                    ...outletsList,
                ]);
                setSelectedOutlets(['all']);
            } else {
                // Filter based on selected hotels
                const outletsList = response.data
                    .filter((outlet) =>
                        selectedHotels.includes(outlet.hotel_id)
                    )
                    .map(({ restaurant_name, restaurant_id }) => ({
                        label: restaurant_name,
                        value: restaurant_id,
                    }));
                setOutlets([
                    { label: 'All Restaurants', value: 'all' },
                    ...outletsList,
                ]);
                setSelectedOutlets(['all']);
            }
        } catch (error) {
            message.error('Failed to fetch outlets. Please try again.');
        }
    };

    const transformAnalyticsData = (data) => {
        if (!data?.length) return { labels: [], datasets: [] };

        const dateFormatter = dateFormatters[intervalType];
        const groupedData = new Map();
        const userTimezone = dayjs.tz.guess();

        // Group and format data by local timezone
        data.forEach((restaurant) => {
            Object.keys(restaurant.count).forEach((date) => {
                const localDate = dayjs.utc(date).tz(userTimezone);
                const groupKey = dateFormatter.groupBy(localDate);
                if (!groupedData.has(groupKey)) {
                    groupedData.set(groupKey, {
                        formattedLabel: dateFormatter.format(localDate),
                        counts: new Map(),
                    });
                }
            });
        });

        data.forEach((restaurant) => {
            Object.entries(restaurant.count).forEach(([date, count]) => {
                const localDate = dayjs.utc(date).tz(userTimezone);
                const groupKey = dateFormatter.groupBy(localDate);
                const existing =
                    groupedData
                        .get(groupKey)
                        .counts.get(restaurant.restaurant_name) || 0;
                groupedData
                    .get(groupKey)
                    .counts.set(restaurant.restaurant_name, existing + count);
            });
        });

        // Sorting dates based on interval type
        const sortedDates = Array.from(groupedData.keys()).sort((a, b) => {
            if (intervalType === 'daily') {
                // Use 'DD MMM' format for daily interval
                return dayjs(a, 'DD MMM').isBefore(dayjs(b, 'DD MMM')) ? -1 : 1;
            } else {
                // Use 'DD-MM-YYYY' format for weekly or monthly intervals
                return dayjs(a, 'DD-MM-YYYY').isBefore(dayjs(b, 'DD-MM-YYYY'))
                    ? -1
                    : 1;
            }
        });

        // Prepare table data
        const tableData = [];
        data.forEach((restaurant) => {
            const restaurantData = {
                restaurant_name: restaurant.restaurant_name,
                hotel_name: restaurant.hotel_name,
            };
            sortedDates.forEach((date) => {
                const count =
                    groupedData
                        .get(date)
                        .counts.get(restaurant.restaurant_name) || '-';
                restaurantData[groupedData.get(date).formattedLabel] = count;
            });
            tableData.push(restaurantData);
        });
        setTableData(tableData);

        // Return data for the chart
        return {
            labels: sortedDates.map(
                (date) => groupedData.get(date).formattedLabel
            ),
            datasets: data.map((restaurant, index) => ({
                label: restaurant.restaurant_name,
                data: sortedDates.map(
                    (date) =>
                        groupedData
                            .get(date)
                            .counts.get(restaurant.restaurant_name) || 0
                ),
                borderColor: `hsl(${(index * 137.508) % 360}, 70%, 50%)`,
                backgroundColor: `hsl(${(index * 137.508) % 360}, 70%, 50%)`,
                fill: false,
                tension: 0.5,
                radius: '5',
                hoverRadius: '5',
            })),
        };
    };

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const isAllSelected = selectedHotels.includes('all');
            const isAllOutletSelected = selectedOutlets.includes('all');

            let startDate, endDate;
            if (dateRange && dateRange[0] && dateRange[1]) {
                startDate = dateRange[0].format('YYYY-MM-DD');
                endDate = dateRange[1].format('YYYY-MM-DD');
            } else {
                // Default to last month if no date range is selected
                startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
                endDate = dayjs().format('YYYY-MM-DD');
            }

            const payload = {
                hotel_ids: isAllSelected ? allHotelIds : selectedHotels,
                restaurant_ids: isAllOutletSelected ? [] : selectedOutlets,
                interval_type: intervalType,
                start_date: startDate,
                end_date: endDate,
            };

            if (!isAllSelected && !selectedHotels.length) {
                message.error('Please select the hotel field');
                return;
            }

            const response = await getAnalyticsData(payload);
            setChartData(transformAnalyticsData(response.data));
        } catch (error) {
            console.log(error);
            message.error('Failed to fetch analytics data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderDataTable = () => {
        if (tableData.length === 0) return null;

        const columns = [
            {
                title: 'Restaurant Name',
                dataIndex: 'restaurant_name',
                key: 'restaurant_name',
                width: 150,
                fixed: 'left',
            },
            ...Object.keys(tableData[0])
                .filter(
                    (key) => key !== 'hotel_name' && key !== 'restaurant_name'
                )
                .map((key) => ({
                    title: key,
                    dataIndex: key,
                    key: key,
                    width: 100, // Adjust this value as needed
                    ellipsis: true,
                })),
        ];

        return (
            <Table
                dataSource={tableData}
                columns={columns}
                rowKey='restaurant_name'
                scroll={{ x: 'max-content' }}
                className='mt-8 w-full'
            />
        );
    };

    const resetFilters = () => {
        setSelectedHotels(['all']);
        setSelectedOutlets(['all']);
        setIntervalType('weekly');
        setDateRange([dayjs().subtract(1, 'month'), dayjs()]);
        setChartData({ labels: [], datasets: [] });
    };

    const handleHotelChange = (value) => {
        if (value.includes('all')) {
            if (selectedHotels.includes('all')) {
                // If 'all' is already selected, remove it
                setSelectedHotels(value.filter((v) => v !== 'all'));
            } else {
                // If 'all' is newly selected, select only 'all'
                setSelectedHotels(['all']);
            }
        } else {
            // If 'all' is not selected, update with the current selection
            setSelectedHotels(value);
        }
    };
    const handleOutletChange = (value) => {
        if (value.includes('all')) {
            if (selectedOutlets.includes('all')) {
                // If 'all' is already selected, remove it
                setSelectedOutlets(value.filter((v) => v !== 'all'));
            } else {
                // If 'all' is newly selected, select only 'all'
                setSelectedOutlets(['all']);
            }
        } else {
            // If 'all' is not selected, update with the current selection
            setSelectedOutlets(value);
        }
    };

    const rangePresets = [
        {
            label: 'Last 7 Days',
            value: [dayjs().add(-7, 'd'), dayjs()],
        },
        {
            label: 'Last 15 Days',
            value: [dayjs().add(-15, 'd'), dayjs()],
        },
        {
            label: 'Last Month',
            value: [dayjs().add(-30, 'd'), dayjs()],
        },
        {
            label: 'Last 3 Months',
            value: [dayjs().add(-90, 'd'), dayjs()],
        },
    ];

    return (
        <div className='bg-img p-2'>
            <Header />
            <div className='guest-form-heading pt-8 pb-8'>Guest Footfall</div>
            <div className='flex flex-col justify-start w-full gap-4 px-4 md:pt-8 md:flex-col md:justify-start md:items-start'>
                <div className='flex flex-col justify-start w-full gap-4 md:pb-8 flex-wrap md:flex-row'>
                    <Select
                        mode='multiple'
                        placeholder='Select Hotels'
                        value={selectedHotels}
                        onChange={handleHotelChange}
                        options={hotels}
                        className='border-gray shadow-sm color-black w-full md:max-w-[262px]'
                    />
                    <Select
                        mode='multiple'
                        placeholder='Select Restaurants'
                        value={selectedOutlets}
                        onChange={handleOutletChange}
                        options={outlets}
                        className='border-gray shadow-sm color-black w-full md:max-w-[262px]'
                    />
                    <RangePicker
                        onChange={setDateRange}
                        value={dateRange}
                        className=' border-gray shadow-sm color-black w-full md:max-w-[264px] md:max-h-[32px]'
                        presets={rangePresets}
                        format={'DD-MM-YYYY'}
                        allowClear={false}
                    />

                    <Select
                        placeholder='Select Interval'
                        value={intervalType}
                        onChange={setIntervalType}
                        options={[
                            { label: 'Daily', value: 'daily' },
                            { label: 'Weekly', value: 'weekly' },
                            { label: 'Monthly', value: 'monthly' },
                        ]}
                        className=' border-gray shadow-sm color-black w-full md:max-w-[264px] md:max-h-[35px]'
                    />

                    <Button type={'primary'} onClick={resetFilters}>
                        Reset
                    </Button>
                </div>

                <div className='chart-container h-[500px] w-full bg-white shadow-md rounded-md'>
                    {loading ? (
                        <div className='h-full flex items-center justify-center'>
                            <Spin size='large' />
                        </div>
                    ) : chartData.labels.length > 0 ? (
                        <Line options={chartOptions} data={chartData} />
                    ) : (
                        <div className='h-full flex items-center justify-center text-gray-400 text-center'>
                            Select hotels, outlets, and date range to view
                            analytics
                        </div>
                    )}
                </div>
                {renderDataTable()}
            </div>
        </div>
    );
};

export default Analytics;
